import { IconButton, Modal } from "@/components/atoms";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { closeBulkImportReport } from "@/redux/slices/bulkImportReport/bulkImportReportSlice";
import { bulkImportReportSelector } from "@/redux/slices/bulkImportReport/selectors";
import { BulkImportReport } from "./BulkImportReport";
import { REPORT_MAX_WIDTH } from "./reportLayout";

/**
 * The import report, in place, without leaving the page (DEV-1215).
 *
 * The in-app way in. Pressing View on the completion snackbar used to navigate to `/bulk-import/:id`,
 * which threw away whatever the user was in the middle of — for a run that was deliberately designed
 * to be walked away from, being yanked somewhere else to read the result is the wrong ending. The page
 * still exists and is still what the completion email links to, since an email reader has no page to
 * be kept on.
 *
 * **Mounted once per portal layout, not by the button that started the import.** The snackbar is a
 * global toast that waits to be dismissed, while `BulkImportTracker` lives inside Book a Trip — which
 * for an operator only exists on the Bookings page. Hosting the modal there meant a toast could
 * outlive the only thing able to answer it.
 *
 * Renders exactly the same `BulkImportReport` the page does, at the same width, under the same title.
 * The only difference between the two is chrome: a close button here, a Back link there.
 */
export const BulkImportReportModal = () => {
  const dispatch = useAppDispatch();
  const { bulkId, accountId } = useAppSelector(bulkImportReportSelector);

  return (
    <Modal
      open={Boolean(bulkId)}
      onClose={() => dispatch(closeBulkImportReport())}
      variant="web-large"
      // web-large caps at max-w-4xl, which squeezes the failures table's four columns. Matched to the
      // page's own cap instead, so the report is laid out identically whichever way it was opened.
      className={REPORT_MAX_WIDTH}
      // No Modal `title`: the report renders its own, so the heading, the file name and the spacing
      // between them come from one place for both hosts rather than being matched by hand.
    >
      {bulkId && (
        <BulkImportReport
          bulkId={bulkId}
          accountId={accountId}
          onLeave={() => dispatch(closeBulkImportReport())}
          headerAction={
            // Sits exactly where the page puts its Back link. The Modal atom has no close button of
            // its own — without this the only ways out would be Escape and the backdrop, and a clean
            // import has no action row to close from either.
            <IconButton
              iconName="close"
              iconSize="lg"
              isCustomIcon
              variant="custom"
              aria-label="Close import report"
              onClick={() => dispatch(closeBulkImportReport())}
            />
          }
        />
      )}
    </Modal>
  );
};
